import React, { createContext, useContext, useState, useEffect } from 'react';

// Available color themes
export const THEMES = {
  emerald: {
    id: 'emerald',
    name: 'Émeraude',
    description: 'Palette par défaut',
    colors: {
      // Vert de marque aligné sur ldwsgroupe.com (#00AD7B = hsl 162 100% 34%)
      primary: '162 100% 34%',
      primaryForeground: '0 0% 100%',
      accent: '162 60% 95%',
      sidebarBg: '#022c22',
      sidebarBgEnd: '#064e3b',
      sidebarActive: '#00AD7B',
      successBg: 'rgba(0, 173, 123, 0.1)',
      successText: '#0a7a59',
      successBorder: '#00AD7B',
    },
    preview: '#00AD7B',
    hex: '#00AD7B',
  },
  blue: {
    id: 'blue',
    name: 'Bleu Océan',
    description: 'Tons bleus professionnels',
    colors: {
      primary: '217 91% 60%',
      primaryForeground: '0 0% 100%',
      accent: '217 60% 95%',
      sidebarBg: '#0f172a',
      sidebarBgEnd: '#1e3a5f',
      sidebarActive: '#3b82f6',
      successBg: 'rgba(59, 130, 246, 0.1)',
      successText: '#2563eb',
      successBorder: '#3b82f6',
    },
    preview: '#3b82f6',
    hex: '#3b82f6',
  },
  violet: {
    id: 'violet',
    name: 'Violet Élégant',
    description: 'Tons violets modernes',
    colors: {
      primary: '262 83% 58%',
      primaryForeground: '0 0% 100%',
      accent: '262 60% 95%',
      sidebarBg: '#1e1b4b',
      sidebarBgEnd: '#312e81',
      sidebarActive: '#8b5cf6',
      successBg: 'rgba(139, 92, 246, 0.1)',
      successText: '#7c3aed',
      successBorder: '#8b5cf6',
    },
    preview: '#8b5cf6',
    hex: '#8b5cf6',
  },
  rose: {
    id: 'rose',
    name: 'Rose Premium',
    description: 'Tons roses sophistiqués',
    colors: {
      primary: '350 89% 60%',
      primaryForeground: '0 0% 100%',
      accent: '350 60% 95%',
      sidebarBg: '#4c0519',
      sidebarBgEnd: '#881337',
      sidebarActive: '#f43f5e',
      successBg: 'rgba(244, 63, 94, 0.1)',
      successText: '#e11d48',
      successBorder: '#f43f5e',
    },
    preview: '#f43f5e',
    hex: '#f43f5e',
  },
  amber: {
    id: 'amber',
    name: 'Ambre Chaleureux',
    description: 'Tons orangés chaleureux',
    colors: {
      primary: '38 92% 50%',
      primaryForeground: '0 0% 100%',
      accent: '38 60% 95%',
      sidebarBg: '#451a03',
      sidebarBgEnd: '#78350f',
      sidebarActive: '#f59e0b',
      successBg: 'rgba(245, 158, 11, 0.1)',
      successText: '#d97706',
      successBorder: '#f59e0b',
    },
    preview: '#f59e0b',
    hex: '#f59e0b',
  },
  slate: {
    id: 'slate',
    name: 'Ardoise Neutre',
    description: 'Palette neutre professionnelle',
    colors: {
      primary: '215 25% 27%',
      primaryForeground: '0 0% 100%',
      accent: '215 20% 95%',
      sidebarBg: '#0f172a',
      sidebarBgEnd: '#1e293b',
      sidebarActive: '#475569',
      successBg: 'rgba(71, 85, 105, 0.1)',
      successText: '#334155',
      successBorder: '#475569',
    },
    preview: '#475569',
    hex: '#475569',
  },
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('app-theme');
    return saved || 'emerald';
  });

  // Apply theme to CSS variables
  useEffect(() => {
    const theme = THEMES[currentTheme];
    if (!theme) return;

    const root = document.documentElement;
    
    // Apply primary color
    root.style.setProperty('--primary', theme.colors.primary);
    root.style.setProperty('--primary-foreground', theme.colors.primaryForeground);
    root.style.setProperty('--accent', theme.colors.accent);
    root.style.setProperty('--ring', theme.colors.primary);
    
    // Apply sidebar colors
    root.style.setProperty('--sidebar-bg', theme.colors.sidebarBg);
    root.style.setProperty('--sidebar-bg-end', theme.colors.sidebarBgEnd);
    root.style.setProperty('--sidebar-active', theme.colors.sidebarActive);
    
    // Apply theme hex color for inline styles
    root.style.setProperty('--theme-color', theme.hex);
    root.style.setProperty('--theme-color-rgb', hexToRgb(theme.hex));
    
    // Success colors (used for positive states)
    root.style.setProperty('--success-bg', theme.colors.successBg);
    root.style.setProperty('--success-text', theme.colors.successText);
    root.style.setProperty('--success-border', theme.colors.successBorder);
    
    // Update chart colors based on theme
    root.style.setProperty('--chart-1', theme.colors.primary);
    
    // Save to localStorage
    localStorage.setItem('app-theme', currentTheme);
  }, [currentTheme]);

  const setTheme = (themeId) => {
    if (THEMES[themeId]) {
      setCurrentTheme(themeId);
    }
  };
  
  // Get current theme object
  const theme = THEMES[currentTheme];

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, themes: THEMES, theme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Helper function to convert hex to RGB
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result 
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '16, 185, 129';
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

export default ThemeProvider;
